import {
  type Team,
  type TeamSelection,
  type UserInfo,
  isTeamSelection,
} from "@repo/api";

type ModalMode = "SELECT" | "SEARCH_COWORKER" | "SEARCH_TEAM";

export type TeamMemberState = UserInfo & { selected: boolean };

export type { TeamSelection };

export type Action =
  | {
      type: "SET_TEAM";
      payload: {
        team: Team | undefined;
        assignedToTeam?: boolean;
        members: TeamMemberState[] | undefined;
      };
    }
  | { type: "TOGGLE_TEAM_MEMBER"; payload: string /* emailId */ }
  | { type: "TOGGLE_ALL_TEAM_MEMBERS"; payload: boolean /* selectAll */ }
  | { type: "SET_COWORKERS"; payload: UserInfo[] | undefined }
  | { type: "SET_MODE"; payload: ModalMode }
  | { type: "SET_COWORKER_SEARCH_RESULT"; payload: UserInfo[] }
  | { type: "SET_TEAM_SEARCH_RESULT"; payload: Team | undefined };

export interface ModalState {
  mode: ModalMode;
  selected: TeamSelection | UserInfo[] | undefined;
  searchResultCoworker: UserInfo[] | undefined;
  searchResultTeam: Team | undefined;
}

export const initialState: ModalState = {
  mode: "SELECT",
  selected: undefined,
  searchResultCoworker: undefined,
  searchResultTeam: undefined,
};

export { isTeamSelection };

export const reducer = (state: typeof initialState, action: Action) => {
  switch (action.type) {
    case "SET_TEAM":
      if (!action.payload.team || !action.payload.members) {
        return { ...state, selected: undefined };
      }
      return {
        ...state,
        selected: {
          kind: "team" as const,
          team: action.payload.team,
          assignedToTeam:
            action.payload.assignedToTeam !== undefined
              ? action.payload.assignedToTeam
              : isTeamSelection(state.selected)
                ? state.selected.assignedToTeam
                : false,
          members: action.payload.members,
        },
      };

    case "TOGGLE_TEAM_MEMBER":
      if (!isTeamSelection(state.selected)) {
        return state;
      }
      return {
        ...state,
        selected: {
          ...state.selected,
          members: state.selected.members.map((m) =>
            m.emailId === action.payload ? { ...m, selected: !m.selected } : m
          ),
        },
      };

    case "TOGGLE_ALL_TEAM_MEMBERS":
      if (!isTeamSelection(state.selected)) {
        return state;
      }
      return {
        ...state,
        selected: {
          ...state.selected,
          members: state.selected.members.map((m) => ({
            ...m,
            selected: action.payload,
          })),
        },
      };

    case "SET_COWORKERS":
      return {
        ...state,
        selected: action.payload,
      };

    case "SET_MODE":
      return {
        ...state,
        mode: action.payload,
      };

    case "SET_COWORKER_SEARCH_RESULT":
      return {
        ...state,
        searchResultCoworker: action.payload,
        searchResultTeam: undefined,
      };

    case "SET_TEAM_SEARCH_RESULT":
      return {
        ...state,
        searchResultCoworker: undefined,
        searchResultTeam: action.payload,
      };
  }

  return state;
};
